import React, { useEffect, useState } from "react";
import {
  listenToGamesByEncounter,
  getTeams,
  getEncounters,
  updateSetScore,
  deleteGame,
  deleteEncounter,
  addReaction,
  getReactions,
} from "../services/firestoreService";
import {
  Container,
  Typography,
  Grid,
  IconButton,
  TextField,
  MenuItem,
  Card,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";

import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";

const GameList = ({ user, role }) => {
  const [games, setGames] = useState([]);
  const [encounters, setEncounters] = useState([]);
  const [pastEncounters, setPastEncounters] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedEncounter, setSelectedEncounter] = useState("all");
  const [openDialog, setOpenDialog] = useState(false);
  const [gameToDelete, setGameToDelete] = useState(null);
  const [reactions, setReactions] = useState({});
  const [editMode, setEditMode] = useState({});
  const [editedScores, setEditedScores] = useState({});
  const emojis = ["👍", "🔥", "😍"];

  useEffect(() => {
    games.forEach(async (game) => {
      const gameReactions = await getReactions(game.id);
      setReactions((prev) => ({ ...prev, [game.id]: gameReactions }));
    });
  }, [games]);

  const handleReaction = async (gameId, emoji) => {
    if (!user) {
      alert("Bitte melde dich an, um zu reagieren!");
      return;
    }

    const result = await addReaction(gameId, user.uid, emoji);

    setReactions((prev) => {
      const updatedReactions = { ...prev };
      if (!updatedReactions[gameId]) updatedReactions[gameId] = [];

      // Entfernen, falls die gleiche Reaktion nochmal gewählt wurde
      if (result === "removed") {
        updatedReactions[gameId] = updatedReactions[gameId].filter(
          (r) => r.id !== user.uid
        );
      } else {
        // Alte Reaktion des Nutzers entfernen & neue hinzufügen
        updatedReactions[gameId] = updatedReactions[gameId].filter(
          (r) => r.id !== user.uid
        );
        updatedReactions[gameId].push({ id: user.uid, emoji });
      }

      return updatedReactions;
    });
  };

  useEffect(() => {
    const fetchEncountersAndTeams = async () => {
      try {
        const teamsData = await getTeams();
        console.log("Teams geladen:", teamsData);
        setTeams(teamsData);

        const encountersData = await getEncounters();
        const now = new Date();
        const cutoff = new Date(now.getTime() - 48 * 60 * 60 * 1000); // 48 Stunden zurück

        const enrichedEncounters = encountersData.map((encounter) => ({
          ...encounter,
          team1Name:
            teamsData.find((team) => team.id === encounter.team1)?.name ||
            "Unbekannt",
          team2Name:
            teamsData.find((team) => team.id === encounter.team2)?.name ||
            "Unbekannt",
          date: new Date(encounter.date),
        }));

        // Begegnungen nach Alter filtern
        const activeEncounters = enrichedEncounters.filter(
          (encounter) => encounter.date >= cutoff
        );
        const pastEncounters = enrichedEncounters.filter(
          (encounter) => encounter.date < cutoff
        );

        setEncounters(activeEncounters);
        setPastEncounters(pastEncounters);
      } catch (error) {
        console.error("Fehler beim Laden von Begegnungen oder Teams:", error);
      }
    };

    fetchEncountersAndTeams();
  }, []);

  useEffect(() => {
    let unsubscribe;
    if (selectedEncounter === "all") {
      unsubscribe = listenToGamesByEncounter("all", setGames);
    } else {
      unsubscribe = listenToGamesByEncounter(selectedEncounter, setGames);
    }
    return () => unsubscribe();
  }, [selectedEncounter]);

  const handleDeleteConfirmation = (gameId) => {
    const confirmDelete = window.confirm(
      "Möchtest du dieses Spiel wirklich löschen?"
    );
    if (confirmDelete) {
      deleteGame(gameId);
    }
  };

  const confirmDeleteGame = async () => {
    if (gameToDelete) {
      await deleteGame(gameToDelete);
      setOpenDialog(false);
      setGameToDelete(null);
    }
  };
  const isSetEditable = (game, index) => {
    if (index === 0) return true;
    return game.sets[index - 1].winner !== null;
  };

  const handleDeleteEncounter = async (encounterId) => {
    const confirmDelete = window.confirm(
      "Möchtest du diese Begegnung wirklich löschen?"
    );
    if (confirmDelete) {
      await deleteEncounter(encounterId);
      setEncounters((prevEncounters) =>
        prevEncounters.filter((encounter) => encounter.id !== encounterId)
      );
    }
  };
  const getGroupedReactions = (reactions) => {
    return reactions.reduce((acc, reaction) => {
      acc[reaction.emoji] = (acc[reaction.emoji] || 0) + 1;
      return acc;
    }, {});
  };

  const toggleEditMode = (gameId, setIndex, score1, score2) => {
    setEditMode((prev) => ({
      ...prev,
      [`${gameId}-${setIndex}`]: !prev[`${gameId}-${setIndex}`],
    }));
    setEditedScores((prev) => ({
      ...prev,
      [`${gameId}-${setIndex}`]: { score1, score2 },
    }));
  };

  const handleScoreChange = (gameId, setIndex, player, value) => {
    setEditedScores((prev) => ({
      ...prev,
      [`${gameId}-${setIndex}`]: {
        ...prev[`${gameId}-${setIndex}`],
        [player === 1 ? "score1" : "score2"]: isNaN(value) ? 0 : value, // Falls leer -> 0 setzen
      },
    }));
  };

  const saveEditedScore = async (gameId, setIndex) => {
    if (!editedScores[`${gameId}-${setIndex}`]) return;

    const gameIndex = games.findIndex((game) => game.id === gameId);
    if (gameIndex === -1) return;

    const currentGame = games[gameIndex];
    const currentSet = currentGame.sets[setIndex];

    const newScore1 =
      editedScores[`${gameId}-${setIndex}`]?.score1 ?? currentSet.score1;
    const newScore2 =
      editedScores[`${gameId}-${setIndex}`]?.score2 ?? currentSet.score2;

    // Direkte Aktualisierung im Backend, ohne Addition
    await updateSetScore(gameId, setIndex, 1, newScore1);
    await updateSetScore(gameId, setIndex, 2, newScore2);

    // Lokale Updates setzen
    const updatedGames = [...games];
    updatedGames[gameIndex].sets[setIndex].score1 = newScore1;
    updatedGames[gameIndex].sets[setIndex].score2 = newScore2;
    setGames(updatedGames);

    setEditMode((prev) => ({ ...prev, [`${gameId}-${setIndex}`]: false }));
  };

  return (
    <Container maxWidth="md">
      <Typography variant="h4" gutterBottom align="center">
        HTC Live Match
      </Typography>

      {/* Begegnungsauswahl */}
      <TextField
        select
        label="Begegnung auswählen"
        fullWidth
        value={selectedEncounter}
        onChange={(e) => setSelectedEncounter(e.target.value)}
        sx={{ mb: 3 }}
      >
        <MenuItem value="all">Alle Begegnungen</MenuItem>
        {encounters.map((encounter) => (
          <MenuItem key={encounter.id} value={encounter.id}>
            {`${encounter.team1Name} vs ${encounter.team2Name}`}
          </MenuItem>
        ))}
      </TextField>

      {encounters.map((encounter) => {
        const encounterGames = games.filter(
          (game) => game.encounterId === encounter.id
        );
        const team1Wins = encounterGames.filter(
          (game) => game.status === "finished" && game.winner === game.player1
        ).length;
        const team2Wins = encounterGames.filter(
          (game) => game.status === "finished" && game.winner === game.player2
        ).length;

        return (
          <Box
            key={encounter.id}
            sx={{ display: "flex", alignItems: "center", mb: 2 }}
          >
            <Accordion sx={{ flexGrow: 1 }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                  {`${encounter.team1Name} vs ${encounter.team2Name} (${team1Wins}:${team2Wins})`}
                </Typography>
                {role === "admin" && (
                  <IconButton
                    onClick={() => handleDeleteEncounter(encounter.id)}
                    color="error"
                    sx={{ ml: 1 }} // Falls nötig, etwas Abstand nach links geben
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </AccordionSummary>

              <AccordionDetails>
                {encounterGames.length === 0 ? (
                  <>
                    <Typography align="center">
                      Keine Spiele vorhanden
                    </Typography>
                  </>
                ) : (
                  encounterGames.map((game) => {
                    const setsWonByPlayer1 = game.sets.filter(
                      (set) => set.winner === game.player1
                    ).length;
                    const setsWonByPlayer2 = game.sets.filter(
                      (set) => set.winner === game.player2
                    ).length;

                    const showTiebreak =
                      setsWonByPlayer1 === 1 && setsWonByPlayer2 === 1;

                    return (
                      <Card key={game.id} sx={{ mb: 2, p: 2 }}>
                        <Typography variant="h6" align="center">
                          {game.player1} vs {game.player2}
                        </Typography>

                        {game.sets.map((set, index) => {
                          const score1 = set.score1;
                          const score2 = set.score2;
                          const isEditing = editMode[`${game.id}-${index}`];

                          // Prüfen, ob der Satz gewonnen wurde (normale Sätze: 6 Punkte mit 2 Vorsprung oder Tiebreak bis 10 mit 2 Vorsprung)
                          const setFinished =
                            index === 2
                              ? (score1 >= 10 || score2 >= 10) &&
                                Math.abs(score1 - score2) >= 2
                              : (score1 >= 6 && score1 - score2 >= 2) ||
                                (score2 >= 6 && score2 - score1 >= 2) ||
                                score1 === 7 ||
                                score2 === 7;

                          // Zeige den dritten Satz immer an, wenn er existiert (auch nach Gewinn)
                          if (
                            index === 2 &&
                            !(
                              setsWonByPlayer1 === 1 && setsWonByPlayer2 === 1
                            ) &&
                            set.score1 === 0 &&
                            set.score2 === 0
                          ) {
                            return null;
                          }

                          // Sperren der Bearbeitung nach Spielende
                          const disableEditing =
                            setFinished || game.status === "finished";
                          return (
                            <Grid
                              container
                              key={index}
                              spacing={2}
                              alignItems="center"
                              justifyContent="center"
                            >
                              <Grid item xs={1}>
                                {set.winner && user && !isEditing && (
                                  <IconButton
                                    onClick={() =>
                                      toggleEditMode(
                                        game.id,
                                        index,
                                        set.score1,
                                        set.score2
                                      )
                                    }
                                  >
                                    <EditIcon />
                                  </IconButton>
                                )}
                              </Grid>
                              <Grid item xs={1}>
                                {(role === "htc" || role === "admin") && (
                                  <>
                                    <IconButton
                                      color="primary"
                                      onClick={() =>
                                        updateSetScore(game.id, index, 1, true)
                                      }
                                      disabled={
                                        disableEditing ||
                                        !isSetEditable(game, index)
                                      }
                                    >
                                      <AddCircleOutlineIcon />
                                    </IconButton>
                                    <IconButton
                                      color="secondary"
                                      onClick={() =>
                                        updateSetScore(game.id, index, 1, false)
                                      }
                                      disabled={
                                        disableEditing ||
                                        !isSetEditable(game, index) ||
                                        score1 === 0
                                      }
                                    >
                                      <RemoveCircleOutlineIcon />
                                    </IconButton>
                                  </>
                                )}
                              </Grid>

                              <Grid item xs={3}>
                                {isEditing ? (
                                  <TextField
                                    type="number"
                                    value={
                                      editedScores[`${game.id}-${index}`]
                                        ?.score1 || set.score1
                                    }
                                    onChange={(e) =>
                                      handleScoreChange(
                                        game.id,
                                        index,
                                        1,
                                        parseInt(e.target.value) || 0
                                      )
                                    }
                                  />
                                ) : (
                                  <Typography
                                    variant="h6"
                                    align="center"
                                    style={{
                                      color:
                                        set.winner === game.player1
                                          ? "green"
                                          : "inherit",
                                    }}
                                  >
                                    {set.score1}
                                  </Typography>
                                )}
                              </Grid>

                              <Grid item xs={2}>
                                <Typography variant="h6" align="center">
                                  {index + 1}
                                </Typography>
                              </Grid>

                              <Grid item xs={3}>
                                {isEditing ? (
                                  <TextField
                                    type="number"
                                    value={
                                      editedScores[`${game.id}-${index}`]
                                        ?.score2 || set.score2
                                    }
                                    onChange={(e) =>
                                      handleScoreChange(
                                        game.id,
                                        index,
                                        2,
                                        parseInt(e.target.value) || 0
                                      )
                                    }
                                  />
                                ) : (
                                  <Typography
                                    variant="h6"
                                    align="center"
                                    style={{
                                      color:
                                        set.winner === game.player2
                                          ? "green"
                                          : "inherit",
                                    }}
                                  >
                                    {set.score2}
                                  </Typography>
                                )}
                              </Grid>

                              <Grid item xs={1}>
                                {user && isEditing && (
                                  <IconButton
                                    onClick={() =>
                                      saveEditedScore(game.id, index)
                                    }
                                  >
                                    <SaveIcon />
                                  </IconButton>
                                )}
                              </Grid>
                              <Grid item xs={1}>
                                {(role === "htc" || role === "admin") && (
                                  <>
                                    <IconButton
                                      color="primary"
                                      onClick={() =>
                                        updateSetScore(game.id, index, 2, true)
                                      }
                                      disabled={
                                        disableEditing ||
                                        !isSetEditable(game, index)
                                      }
                                    >
                                      <AddCircleOutlineIcon />
                                    </IconButton>
                                    <IconButton
                                      color="secondary"
                                      onClick={() =>
                                        updateSetScore(game.id, index, 2, false)
                                      }
                                      disabled={
                                        disableEditing ||
                                        !isSetEditable(game, index) ||
                                        score2 === 0
                                      }
                                    >
                                      <RemoveCircleOutlineIcon />
                                    </IconButton>
                                  </>
                                )}
                              </Grid>
                            </Grid>
                          );
                        })}

                        {game.status === "finished" && (
                          <Typography variant="h6" align="center" color="green">
                            Gewinner: {game.winner}
                          </Typography>
                        )}
                        {role === "admin" && (
                          <Button
                            variant="contained"
                            color="error"
                            onClick={() => handleDeleteConfirmation(game.id)}
                            fullWidth
                          >
                            Spiel löschen
                          </Button>
                        )}
                        <Grid container justifyContent="center">
                          {emojis.map((emoji) => (
                            <IconButton
                              key={emoji}
                              onClick={() => handleReaction(game.id, emoji)}
                            >
                              {emoji}
                            </IconButton>
                          ))}
                        </Grid>

                        <Grid container justifyContent="center">
                          {Object.entries(
                            getGroupedReactions(reactions[game.id] || [])
                          ).map(([emoji, count]) => (
                            <Typography key={emoji} sx={{ mx: 1 }}>
                              {emoji} x{count}
                            </Typography>
                          ))}
                        </Grid>
                      </Card>
                    );
                  })
                )}
                      {user && (
                          <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            sx={{ mt: 2 }}
                            onClick={() => window.openGameDialog("game")}
                          >
                            Spiel hinzufügen
                          </Button>
                        )}
              </AccordionDetails>
            </Accordion>
          </Box>
        );
      })}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Spiel löschen</DialogTitle>
        <DialogContent>
          <Typography>Möchten Sie dieses Spiel wirklich löschen?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="secondary">
            Abbrechen
          </Button>
          <Button onClick={confirmDeleteGame} color="error">
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default GameList;
